import {
  PaymentPolicyType,
  type CancellationPolicy,
  type ReservationLocation,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

export type EffectiveProtectionMethod = 'NO_PAYMENT_METHOD' | 'PREPAYMENT' | 'CARD_GUARANTEE'

/**
 * Protection is active when the location (or reservation snapshot) has a cancellationFee
 * and the party size meets the fee's minPartySize threshold.
 * Config-driven only — never gated on the tableReservationsCancellationFee flag in OOI.
 */
export function isProtectionActive(
  cancellationPolicy: CancellationPolicy | undefined | null,
  partySize?: number | null,
): boolean {
  const fee = cancellationPolicy?.cancellationFee

  if (!fee) {
    return false
  }

  const minPartySize = fee.minPartySize ?? 1

  return (partySize ?? 0) >= minPartySize
}

/**
 * Derive the effective protection method from cancellationFee presence and whether the location
 */
export function effectiveProtectionMethod(
  cancellationPolicy: CancellationPolicy | undefined | null,
  hasConfiguredPrepayment: boolean,
  partySize?: number | null,
): EffectiveProtectionMethod {
  if (!isProtectionActive(cancellationPolicy, partySize)) {
    return 'NO_PAYMENT_METHOD'
  }

  if (hasConfiguredPrepayment) {
    return 'PREPAYMENT'
  }

  return 'CARD_GUARANTEE'
}

export function configuredProtectionMethod(
  cancellationPolicy: CancellationPolicy | undefined | null,
  hasConfiguredPrepayment: boolean,
): EffectiveProtectionMethod {
  if (hasConfiguredPrepayment) {
    return 'PREPAYMENT'
  }

  if (cancellationPolicy?.cancellationFee) {
    return 'CARD_GUARANTEE'
  }

  return 'NO_PAYMENT_METHOD'
}

export const getGoverningCancellationPolicy = (
  reservationLocation: ReservationLocation | undefined | null,
  experienceId: string | undefined | null,
): CancellationPolicy | undefined =>
  experienceId ? undefined : reservationLocation?.configuration?.cancellationPolicy

export function hasPrepayment(reservationLocation?: ReservationLocation): boolean {
  const type = reservationLocation?.configuration?.paymentPolicy?.paymentPolicyType

  return type === PaymentPolicyType.PER_GUEST || type === PaymentPolicyType.PER_RESERVATION
}

export const isCancellationPolicyDisclosureShown = (
  cancellationPolicy: CancellationPolicy | undefined | null,
  isCancellationPolicyActive: boolean,
  isCancellationFeeEnabled: boolean,
): boolean =>
  !isCancellationFeeEnabled && isCancellationPolicyActive && !!cancellationPolicy?.cancellationFee
